import { CartItem, CategoryOrderItem, PillConfigurations } from '@msc-b2c/interfaces/types';
import { AllTypes } from '../productcarousel/ProductCarousel';
import { getPromoThreshold, managePlurals, replacePlaceholders } from '@msc-b2c/shared-utils';
import {
  GetBookingMyMscResponse,
  MyAreaPillsConfiguration,
  MyMscExcursion,
} from '@msc-b2c/interfaces/interfaces';
import { getAlreadyBookedPassengerId } from '../postbookingresults/utils/PostBookingResultsUtilities';
import { isItemOnCart } from './myAreaCartUtils';
import { PageType } from '@msc-b2c/interfaces/enums';

function isMyMscExcursion(card: AllTypes): card is MyMscExcursion {
  return 'activityDetails' in card;
}

function hasServiceCode(value?: string | string[]): boolean {
  if (!value) return false;
  return Array.isArray(value) ? value.length > 0 : value.trim().length > 0;
}

function isObsAllInclusiveOrCombinedPackage(card: AllTypes): boolean {
  if (isMyMscExcursion(card)) return false;
  const serviceCodes = card.prepaidItems?.serviceCodes;
  return (
    !!card.prepaidItems?.isAllInclusive ||
    hasServiceCode(serviceCodes?.adults) ||
    hasServiceCode(serviceCodes?.minors)
  );
}

function isItemAlreadyInCart(card: AllTypes, cart?: CartItem[]): boolean {
  if (!cart?.length) return false;
  if (isObsAllInclusiveOrCombinedPackage(card)) return false;
  return isItemOnCart(card, cart);
}

function isServiceAlreadyInCart(card: AllTypes, cart?: CartItem[]): boolean {
  if (!cart?.length) return false;
  if (!isObsAllInclusiveOrCombinedPackage(card)) return false;
  return isItemOnCart(card, cart);
}

function isServiceOrItemInCart(
  pillConfigurations: PillConfigurations[],
  card: AllTypes,
  cart?: CartItem[]
): boolean {
  if (isServiceAlreadyInCart(card, cart)) {
    return pillConfigurations.some((pill) => pill.pillType === 'serviceAlreadyInCart');
  }
  if (isItemAlreadyInCart(card, cart)) {
    return pillConfigurations.some((pill) => pill.pillType === 'itemAlreadyInCart');
  }

  return false;
}

export const filterPills = (
  card: AllTypes,
  pillConfigurations: PillConfigurations[],
  pillsVisibilityConfig: MyAreaPillsConfiguration,
  recommendedPills?: CategoryOrderItem[],
  booking?: GetBookingMyMscResponse,
  cart?: CartItem[],
  pageType: PageType = PageType.OTHER
): PillConfigurations[] => {
  // Prioritize booked pill if enabled
  const isCartOrCheckoutPage = pageType === 'CART_PAGE' || pageType === 'CHECKOUT_PAGE';
  if (
    !isCartOrCheckoutPage &&
    !isServiceOrItemInCart(pillConfigurations, card, cart) &&
    pillsVisibilityConfig.bookedPillEnabled &&
    booking &&
    isBookedPill(card, booking)
  ) {
    const bookedPill = pillConfigurations.find(({ pillType }) => pillType === 'booked');
    if (bookedPill && isPillConfigured(bookedPill)) {
      return [bookedPill];
    }
  }

  const promoStatus = getPromoStatus(card, pillsVisibilityConfig);
  const validPills: PillConfigurations[] = [];

  pillConfigurations.forEach((pill) => {
    switch (pill.pillType) {
      case 'itemAlreadyInCart':
        if (
          pageType !== PageType.CART_PAGE &&
          pageType !== PageType.CHECKOUT_PAGE &&
          isItemAlreadyInCart(card, cart)
        ) {
          validPills.push(pill);
        }
        break;
      case 'serviceAlreadyInCart':
        if (
          pageType !== PageType.CART_PAGE &&
          pageType !== PageType.CHECKOUT_PAGE &&
          isServiceAlreadyInCart(card, cart)
        ) {
          validPills.push(pill);
        }
        break;
      case 'limitedSeats':
        if (isLimitedSeatsCard(card)) validPills.push(pill);
        break;

      case 'recommended':
        if (isRecommendedCard(card, recommendedPills)) validPills.push(pill);
        break;

      case 'promo':
        if (isPromoCard(card, pillsVisibilityConfig)) validPills.push(pill);
        break;

      case 'countDownDays':
      case 'countDownHours':
        if (
          (pill.pillType === 'countDownDays' &&
            isCountDownDays(card, pillsVisibilityConfig, 'countDownDays')) ||
          (pill.pillType === 'countDownHours' &&
            isCountDownHours(card, pillsVisibilityConfig, 'countDownHours'))
        ) {
          const countdownPhrase = pill.pillLabel;
          if (!countdownPhrase) return;

          const label =
            pill.pillType === 'countDownDays'
              ? replacePlaceholders(managePlurals(promoStatus.dayFromEndDate, countdownPhrase), {
                  remaining_days: promoStatus.dayFromEndDate.toString(),
                })
              : replacePlaceholders(
                  managePlurals(Math.max(1, promoStatus.timeDiffHours), countdownPhrase),
                  { remaining_hours: Math.max(1, promoStatus.timeDiffHours).toString() }
                );

          validPills.push({ ...pill, pillLabel: label });
        }
        break;

      default:
        break; // other pill types if needed
    }
  });
  return validPills;
};

export function hasCardID(
  items: CategoryOrderItem[],
  cardID: string,
  typeFilter?: string
): boolean {
  return items.some((item) => {
    if (typeFilter && item.type !== typeFilter) return false;
    return item.cardTypeCodes.includes(cardID);
  });
}

export const isBookedPill = (card: AllTypes, booking: GetBookingMyMscResponse): boolean => {
  const bookedPassengers = getAlreadyBookedPassengerId(card, booking, isMyMscExcursion(card));

  return bookedPassengers.length > 0;
};

export const isPillConfigured = (pill: PillConfigurations): boolean =>
  !!pill.backgroundColor && !!pill.fontColor && !!pill.pillLabel && !!pill.pillType;

type PromoStatus = {
  isInPromoPeriod: boolean;
  timeDiffHours: number;
  dayFromEndDate: number;
  threshold: number;
  isPromoVisible: boolean;
  isCountdownVisible: boolean;
};

export const getPromoStatus = (
  card: AllTypes,
  pillsVisibilityConfig: MyAreaPillsConfiguration
): PromoStatus => {
  const promoStart = new Date(card?.promotion?.validFrom ?? '');
  const promoEnd = new Date(card?.promotion?.validTo ?? '');
  const today = new Date();

  const isInPromoPeriod = today >= promoStart && today <= promoEnd;

  const timeDiffMs = promoEnd.getTime() - today.getTime();
  const timeDiffHours = Math.max(0, Math.floor(timeDiffMs / (1000 * 60 * 60)));
  const dayFromEndDate = Math.floor(timeDiffHours / 24);
  const threshold = getPromoThreshold() ?? 0;

  const isPromoVisible =
    pillsVisibilityConfig.promoPillEnabled &&
    isInPromoPeriod &&
    (threshold === 0 || dayFromEndDate > threshold);

  const isCountdownVisible =
    pillsVisibilityConfig.countdownPillEnabled &&
    isInPromoPeriod &&
    threshold !== 0 &&
    dayFromEndDate <= threshold;

  return {
    isInPromoPeriod,
    timeDiffHours,
    dayFromEndDate,
    threshold,
    isPromoVisible,
    isCountdownVisible,
  };
};

export const isLimitedSeatsCard = (card: AllTypes): boolean => {
  return isMyMscExcursion(card) && !!card.activityDetails?.isLimitedSeats;
};

export const isRecommendedCard = (
  card: AllTypes,
  recommendedPills?: CategoryOrderItem[]
): boolean => {
  return (
    !!recommendedPills &&
    hasCardID(
      recommendedPills,
      card.code ?? '',
      isMyMscExcursion(card) ? undefined : card.type.code
    )
  );
};

export const isPromoCard = (
  card: AllTypes,
  pillsVisibilityConfig: MyAreaPillsConfiguration
): boolean => {
  return getPromoStatus(card, pillsVisibilityConfig).isPromoVisible;
};

export const isCountDownDays = (
  card: AllTypes,
  pillsVisibilityConfig: MyAreaPillsConfiguration,
  pillType: 'countDownDays'
): boolean => {
  const { isCountdownVisible, timeDiffHours } = getPromoStatus(card, pillsVisibilityConfig);

  if (!isCountdownVisible) return false;

  return pillType === 'countDownDays' && timeDiffHours > 24;
};

export const isCountDownHours = (
  card: AllTypes,
  pillsVisibilityConfig: MyAreaPillsConfiguration,
  pillType: 'countDownHours'
): boolean => {
  const { isCountdownVisible, timeDiffHours } = getPromoStatus(card, pillsVisibilityConfig);

  if (!isCountdownVisible) return false;

  return pillType === 'countDownHours' && timeDiffHours >= 1 && timeDiffHours <= 24;
};

export const getPillPriority = (
  card: AllTypes,
  pillsVisibilityConfig: MyAreaPillsConfiguration,
  cardSortingList: string[],
  recommendedPills?: CategoryOrderItem[],
  cart?: CartItem[]
): number => {
  const index = cardSortingList.findIndex((type) => {
    if (type === 'itemAlreadyInCart' || type === 'serviceAlreadyInCart') {
      return false;
    }

    return (
      (type === 'countDownHours' &&
        isCountDownHours(card, pillsVisibilityConfig, 'countDownHours')) ||
      (type === 'countDownDays' && isCountDownDays(card, pillsVisibilityConfig, 'countDownDays')) ||
      (type === 'promo' && isPromoCard(card, pillsVisibilityConfig)) ||
      (type === 'limitedSeats' && isLimitedSeatsCard(card)) ||
      (type === 'recommended' && isRecommendedCard(card, recommendedPills))
    );
  });

  return index === -1
    ? cardSortingList.some((type) => type === '')
      ? cardSortingList.indexOf('')
      : cardSortingList.length
    : index;
};
